import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import config from '../config/config';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Package, Truck, CheckCircle, XCircle, Clock, Search, Filter, Eye, RotateCcw,
  Download, X, CreditCard, ShoppingBag, MapPin, Star, Zap, ArrowRight
} from 'lucide-react';

import FlashBar from "../components/FlashBar";
import NavigationBar from "../components/NavigationBar";
import FooterSection from "../components/FooterSection";
import CartSection from "../components/CartSection";
import SearchWidget from "../components/SearchWidget";
import ShareableNav from "../components/ShareableNav";

// ────────────────────────────────────────────────────────────────────────────
// Cache Configuration
// ────────────────────────────────────────────────────────────────────────────
const CACHE_KEY_LAYOUT = 'layout_settings_cache_orders-01';
const CACHE_KEY_ORDERS  = 'orders_cache_user_orders-01';
const CACHE_TTL = 90 * 1000;
const ORDERS_CACHE_TTL = 5 * 60 * 1000;

// ────────────────────────────────────────────────────────────────────────────
// ORDER PROGRESS STEPS DEFINITION
// ────────────────────────────────────────────────────────────────────────────
const ORDER_STEPS = [
  {
    key: 'pending',
    actionTime: '',
    label: 'Order Placed',
    sublabel: '',
    icon: ShoppingBag,
    color: '#10b981',
  },
  {
    key: 'processing',
    label: 'Processing',
    actionTime: '',
    sublabel: '',
    icon: Zap,
    color: '#3b82f6',
  },
  {
    key: 'ready to pay',
    label: 'Ready to Pay',
    actionTime: '',
    sublabel: '',
    icon: CreditCard,
    color: '#f59e0b',
  },
  {
    key: 'packaging',
    label: 'Packaging',
    sublabel: '',
    actionTime: '',
    icon: Package,
    color: '#8b5cf6',
  },
  {
    key: 'shipped',
    label: 'In Transit',
    sublabel: '',
    actionTime: '',
    icon: Truck,
    color: '#06b6d4',
  },
  {
    key: 'delivered',
    label: 'Delivered',
    sublabel: '',
    icon: Star,
    actionTime: '',
    color: '#10b981',
  },
];

const CANCELED_STEP = {
  key: 'canceled',
  label: 'Cancelled',
  sublabel: '',
  icon: XCircle,
  color: '#ef4444',
};

function getStepIndex(status) {
  const s = (status || '').toLowerCase().trim();
  if (s === 'canceled' || s === 'cancelled') return -1;
  const idx = ORDER_STEPS.findIndex(step => step.key === s);
  return idx === -1 ? 0 : idx;
}

// ────────────────────────────────────────────────────────────────────────────
// FORMAT TIMESTAMP
// Accepts "HH:MM:SS" or a full datetime string, returns "h:MMam/pm"
// Returns null if the value is empty, null, or unparseable
// ────────────────────────────────────────────────────────────────────────────
function formatTimestamp(ts) {
  if (!ts || ts === '' || ts === null) return null;
  const match = String(ts).match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return null;
  let h = parseInt(match[1], 10);
  const m = match[2];
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}:${m}${ampm}`;
}

// ────────────────────────────────────────────────────────────────────────────
// ANIMATED ORDER PROGRESS BAR COMPONENT
//
// Behaviour:
//   • Desktop: all 6 steps are fully visible side-by-side at natural width.
//   • Mobile : the track is horizontally scrollable (no visible scrollbar).
//              After the entrance animation completes the active step is
//              smooth-scrolled into the horizontal center of the viewport.
//   • Timestamps: pulled from the `timeStamps` prop (matches API shape) and
//              rendered in a small line directly below each step label.
//              A dash is shown for steps that have been reached but have no
//              recorded time; future steps show nothing.
// ────────────────────────────────────────────────────────────────────────────
const OrderProgressBar = ({ status, timeStamps = {}, compact = false }) => {
  const [animated, setAnimated] = useState(false);
  const scrollRef   = useRef(null);   // the scrollable wrapper div
  const activeStepRef = useRef(null); // the active step column div

  const isCanceled = (status || '').toLowerCase().includes('cancel');
  const currentStep = isCanceled ? -1 : getStepIndex(status);
  const steps = ORDER_STEPS;

  // Trigger entrance animation shortly after mount
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Once animated, scroll the active step into the horizontal center
  useEffect(() => {
    if (!animated) return;
    const scrollEl  = scrollRef.current;
    const activeEl  = activeStepRef.current;
    if (!scrollEl || !activeEl) return;

    const timer = setTimeout(() => {
      const targetScrollLeft =
        activeEl.offsetLeft - scrollEl.clientWidth / 2 + activeEl.offsetWidth / 2;
      scrollEl.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    }, 500); // slight delay so the fill animation is already running

    return () => clearTimeout(timer);
  }, [animated, currentStep]);

  const progressPercent =
    currentStep < 0 ? 0 : (currentStep / (steps.length - 1)) * 100;

  // ── Cancelled state ──────────────────────────────────────────────────────
  if (isCanceled) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: compact ? '10px 14px' : '14px 20px',
        background: '#fef2f2',
        borderRadius: 12,
        border: '1px solid #fecaca',
      }}>
        <XCircle size={18} color="#ef4444" />
        <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 600, color: '#ef4444', fontSize: 14 }}>
          This order was cancelled
        </span>
      </div>
    );
  }

  // Each step column is a fixed pixel width so the connecting line maths
  // stays correct regardless of how many steps are visible at once.
  const STEP_W = 88; // px — wide enough for label + timestamp on one line
  const totalTrackPx = steps.length * STEP_W; // e.g. 6 × 88 = 528 px

  return (
    <div style={{ width: '100%', fontFamily: 'Outfit, sans-serif' }}>
      <style>{`
        @keyframes stepPop {
          0%   { transform: scale(0.5); opacity: 0; }
          60%  { transform: scale(1.15); }
          100% { transform: scale(1);   opacity: 1; }
        }
        @keyframes checkDraw {
          0%   { stroke-dashoffset: 30; }
          100% { stroke-dashoffset: 0;  }
        }
        @keyframes pulseGlow {
          0%,100% { box-shadow: 0 0 0 0   rgba(16,185,129,0.4); }
          50%     { box-shadow: 0 0 0 8px rgba(16,185,129,0);   }
        }
        @keyframes floatLabel {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0);   }
        }
        .progress-step-circle {
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .progress-step-circle:hover { transform: scale(1.1) !important; }

        /* Hide scrollbar on the progress track across browsers */
        .progress-scroll-track {
          overflow-x: auto;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        .progress-scroll-track::-webkit-scrollbar { display: none; }
      `}</style>

      {/* ── Scrollable wrapper ─────────────────────────────────────────── */}
      <div ref={scrollRef} className="progress-scroll-track" style={{ width: '100%' }}>

        {/* Inner track — fixed minimum width so it never collapses on mobile */}
        <div style={{
          position: 'relative',
          minWidth: `${totalTrackPx}px`,
          padding: compact ? '8px 0 4px' : '12px 0 4px',
        }}>

          {/* Grey background line */}
          <div style={{
            position: 'absolute',
            top: compact ? 22 : 28,
            left:  `${STEP_W / 2}px`,
            right: `${STEP_W / 2}px`,
            height: 3,
            background: '#e5e7eb',
            borderRadius: 99,
            zIndex: 0,
          }} />

          {/* Animated colour fill line */}
          <div style={{
            position: 'absolute',
            top: compact ? 22 : 28,
            left: `${STEP_W / 2}px`,
            height: 3,
            borderRadius: 99,
            zIndex: 1,
            width: animated ? `${progressPercent}%` : '0%',
            maxWidth: `calc(100% - ${STEP_W}px)`,
            background: 'linear-gradient(90deg, #10b981, #3b82f6, #8b5cf6)',
            transition: 'width 1.2s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 1px 6px rgba(59,130,246,0.3)',
          }} />

          {/* ── Step columns ──────────────────────────────────────────── */}
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            position: 'relative',
            zIndex: 2,
          }}>
            {steps.map((step, i) => {
              const StepIcon   = step.icon;
              const isCompleted = i < currentStep;
              const isActive    = i === currentStep;
              const isPending   = i > currentStep;
              const delay       = i * 120;

              // ── Timestamp for this step ──────────────────────────────
              // timeStamps keys match ORDER_STEPS keys exactly, e.g.:
              //   { pending: "", processing: "13:14:16", "ready to pay": null, … }
              const rawTs      = timeStamps?.[step.key];
              const displayTime = formatTimestamp(rawTs);

              return (
                <div
                  key={step.key}
                  ref={isActive ? activeStepRef : null}
                  style={{
                    width: `${STEP_W}px`,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: compact ? 5 : 6,
                  }}
                >
                  {/* Circle */}
                  <div
                    className="progress-step-circle"
                    style={{
                      width:  compact ? 38 : 48,
                      height: compact ? 38 : 48,
                      borderRadius: '50%',
                      marginTop: '-7px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: isCompleted
                        ? `linear-gradient(135deg, ${step.color}, ${step.color}cc)`
                        : isActive
                          ? `linear-gradient(135deg, ${step.color}, ${step.color}aa)`
                          : '#f3f4f6',
                      border: isActive
                        ? `2.5px solid ${step.color}`
                        : isCompleted
                          ? 'none'
                          : '2px solid #e5e7eb',
                      boxShadow: isActive
                        ? `0 0 0 4px ${step.color}20, 0 4px 12px ${step.color}30`
                        : isCompleted
                          ? `0 4px 10px ${step.color}30`
                          : 'none',
                      animation: animated && (isCompleted || isActive)
                        ? `stepPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay}ms both${
                            isActive ? `, pulseGlow 2s ease-in-out ${delay + 500}ms infinite` : ''
                          }`
                        : 'none',
                      position: 'relative',
                      cursor: 'default',
                      flexShrink: 0,
                    }}
                  >
                    {isCompleted ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <path
                          d="M5 13l4 4L19 7"
                          stroke="white"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{
                            strokeDasharray: 30,
                            animation: animated
                              ? `checkDraw 0.4s ease ${delay + 200}ms both`
                              : 'none',
                          }}
                        />
                      </svg>
                    ) : (
                      <StepIcon
                        size={compact ? 16 : 20}
                        color={isActive ? 'white' : isPending ? '#9ca3af' : 'white'}
                        strokeWidth={2}
                      />
                    )}

                    {/* Active pulse ring */}
                    {isActive && (
                      <div style={{
                        position: 'absolute',
                        inset: -4,
                        borderRadius: '50%',
                        border: `2px solid ${step.color}`,
                        opacity: 0,
                        animation: `pulseGlow 1.8s ease-in-out infinite`,
                      }} />
                    )}
                  </div>

                  {/* Label + timestamp block */}
                  <div style={{
                    textAlign: 'center',
                    animation: animated
                      ? `floatLabel 0.4s ease ${delay + 300}ms both`
                      : 'none',
                    paddingBottom: 4,
                  }}>
                    {/* Step label */}
                    <div style={{
                      fontSize: compact ? 10 : 11,
                      fontWeight: isActive ? 700 : isCompleted ? 600 : 400,
                      color: isActive
                        ? step.color
                        : isCompleted
                          ? '#374151'
                          : '#9ca3af',
                      letterSpacing: '0.01em',
                      lineHeight: 1.3,
                      whiteSpace: 'nowrap',
                    }}>
                      {step.label}
                    </div>

                    {/* Timestamp — visible once a step is completed or active.
                        Shows the formatted time when available, otherwise a dash. */}
                    {(isCompleted || isActive) && (
                      <div style={{
                        fontSize: 9,
                        marginTop: 2,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        color: isActive
                          ? `${step.color}bb`
                          : '#6b7280',
                      }}>
                        {displayTime ?? '—'}
                      </div>
                    )}

                    {/* Sublabel — only on full (non-compact) mode */}
                    {!compact && (
                      <div style={{
                        fontSize: 10,
                        color: isActive ? `${step.color}aa` : '#c4c9d4',
                        marginTop: 2,
                        whiteSpace: 'nowrap',
                      }}>
                        {step.sublabel}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Active Step Banner ──────────────────────────────────────────── */}
      {(() => {
        const activeStep = steps[currentStep];
        if (!activeStep) return null;
        return (
          <div style={{
            marginTop: 4,
            padding: '10px 16px',
            background: `linear-gradient(135deg, ${activeStep.color}10, ${activeStep.color}05)`,
            border: `1px solid ${activeStep.color}30`,
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            animation: animated ? 'floatLabel 0.5s ease 800ms both' : 'none',
          }}>
            <div style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: activeStep.color,
              flexShrink: 0,
              animation: 'pulseGlow 1.5s ease-in-out infinite',
            }} />
            <div>
              <span style={{ fontWeight: 700, color: activeStep.color, fontSize: 13 }}>
                {activeStep.label}
              </span>
              <span style={{ color: '#6b7280', fontSize: 13, marginLeft: 6 }}>
                — {activeStep.sublabel}
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────────────────
const ShopPage = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const userInfo = localStorage.getItem('userInfor');

  useEffect(() => {
    let isLoggedIn = false;
    if (userInfo && userInfo !== null && userInfo !== 'null') {
      isLoggedIn = true;
    } else {
      navigate('/Authentication');
    }
    if (!isLoggedIn) {
      navigate('/Authentication');
    }
  }, [token, userInfo, order_id]);

  const [isReadyToPay, setIsReadyToPay] = useState(false);
  const [toPayId, setToPayId] = useState(false);
  const [mpesaMessage, setMpesaMessage] = useState('');
  const [mpesaLoading, setMpesaLoading] = useState(null);
  const [flashConfig, setFlashConfig] = useState(null);
  const [navConfig, setNavConfig] = useState(null);
  const [searchConfig, setSearchConfig] = useState(null);
  const [cartConfig, setCartConfig] = useState(null);
  const [footerConfig, setFooterConfig] = useState(null);

  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState([]);
  const [currentOrderArray, setCurrentOrderArray] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [formData, setFormData] = useState({ mpesaPhone: '' });

  const getCached = (key, customTTL = CACHE_TTL) => {
    try {
      const item = localStorage.getItem(key);
      if (!item) return null;
      const { data, ts } = JSON.parse(item);
      if (Date.now() - ts < customTTL) return data;
      localStorage.removeItem(key);
      return null;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  };

  const setCached = (key, data) => {
    try {
      localStorage.setItem(key, JSON.stringify({ data, ts: Date.now() }));
    } catch (e) {}
  };

  const loadOrders = useCallback(async () => {
    if (!token) return;
    const cachedOrders = getCached(CACHE_KEY_ORDERS, ORDERS_CACHE_TTL);
    if (cachedOrders) setOrders(cachedOrders);

    try {
      const ordersRes = await axios.get(
        `${config.API_URL}/api/pullOrders/${token}${order_id ? `/${order_id}` : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const freshOrders = ordersRes.data?.orders || ordersRes.data?.data || ordersRes.data || [];

      if (ordersRes.data?.order_status === 'ready to pay') {
        setCurrentOrder(ordersRes.data?.current_order);
        setCurrentOrderArray(ordersRes.data?.currentOrderArray);
        setToPayId(ordersRes.data?.current_order?.order_number);
        setTimeout(() => {}, 500);
      }

      setOrders(freshOrders);
      setCached(CACHE_KEY_ORDERS, freshOrders);
    } catch (err) {
      console.error("Orders fetch failed:", err);
      if (!cachedOrders) toast.error("Could not load your orders");
    }
  }, [token, order_id]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  setInterval(loadOrders, 300000);

  const tabs = useMemo(() => [
    { key: 'all',          label: 'All Orders',   count: orders.length },
    { key: 'pending',      label: 'Pending',       count: orders.filter(o => o.status?.toLowerCase() === 'pending').length },
    { key: 'processing',   label: 'Processing',    count: orders.filter(o => o.status?.toLowerCase() === 'processing').length },
    { key: 'ready to pay', label: 'Ready To Pay',  count: orders.filter(o => o.status?.toLowerCase() === 'ready to pay').length },
    { key: 'packaging',    label: 'Packaging',     count: orders.filter(o => o.status?.toLowerCase() === 'packaging').length },
    { key: 'shipped',      label: 'Shipped',       count: orders.filter(o => o.status?.toLowerCase() === 'shipped').length },
    { key: 'delivered',    label: 'Delivered',     count: orders.filter(o => o.status?.toLowerCase() === 'delivered').length },
    { key: 'canceled',     label: 'Canceled',      count: orders.filter(o => o.status?.toLowerCase() === 'canceled').length },
  ], [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const matchesTab = activeTab === 'all' || order.status?.toLowerCase() === activeTab;
      const matchesSearch = searchQuery === '' ||
        order.id?.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
        order.products?.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesTab && matchesSearch;
    });
  }, [orders, activeTab, searchQuery]);

  const getStatusConfig = (status) => {
    const key = (status || '').toLowerCase().trim().replace(/\s+/g, '');
    const configs = {
      delivered:  { label: 'Delivered',     icon: CheckCircle, color: 'text-green-600',  bg: 'bg-green-100'  },
      shipped:    { label: 'Shipped',        icon: Truck,       color: 'text-blue-600',   bg: 'bg-blue-100'   },
      packaging:  { label: 'Packaging',      icon: Package,     color: 'text-purple-600', bg: 'bg-purple-100' },
      processing: { label: 'Processing',     icon: Clock,       color: 'text-orange-600', bg: 'bg-orange-100' },
      pending:    { label: 'Pending',        icon: Clock,       color: 'text-orange-700', bg: 'bg-orange-200' },
      readytopay: { label: 'Ready to Pay',   icon: CreditCard,  color: 'text-green-600',  bg: 'bg-green-100'  },
      cancelled:  { label: 'Cancelled',      icon: XCircle,     color: 'text-red-600',    bg: 'bg-red-100'    },
      canceled:   { label: 'Canceled',       icon: XCircle,     color: 'text-red-600',    bg: 'bg-red-100'    },
    };
    return configs[key] || configs.processing;
  };

  const pollPayment = async () => {
    try {
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
      const response = await axios.post(
        config.API_URL + `/api/pollPayment`,
        { phone: formData.mpesaPhone, order_id: selectedOrder?.order_number },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
      if (response.data.status === 'success') {
        setMpesaLoading('success');
        window.location.reload();
        setTimeout(() => window.location.reload(), 4000);
      } else {
        setMpesaLoading('failed-2');
        setMpesaMessage(response.data.message);
      }
    } catch (error) {}
  };

  const handleMpesaPrompt = async () => {
    try {
      setMpesaLoading(0);
      await axios.get(config.API_URL + "/sanctum/csrf-cookie", { withCredentials: true });
      const xsrfToken = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='))?.split('=')[1];
      const response = await axios.post(
        config.API_URL + `/api/handleMpesaPrompt`,
        { phone: formData.mpesaPhone, order_id: order_id || toPayId },
        {
          headers: {
            "Content-Type": "application/json",
            "Accept": "application/json",
            "X-XSRF-TOKEN": decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );
      if (response.data.status === '0') {
        setMpesaLoading(1);
        setInterval(pollPayment, 6000);
      } else {
        setMpesaLoading('failed');
      }
    } catch (error) {}
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);

  return (
    <div className="flex flex-col md:mt-[0px] min-h-screen bg-gray-50">
      <ShareableNav />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&family=Quicksand:wght@400;500;600;700&display=swap');
        html, body {
          font-family: ${config.FONT_FAMILY}, sans-serif !important;
        }
        @keyframes orderCardSlide {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes modalBackdropIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes modalSlideUp {
          from { opacity: 0; transform: translateY(40px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
        @keyframes badgePop {
          0%   { transform: scale(0);   opacity: 0; }
          60%  { transform: scale(1.2);             }
          100% { transform: scale(1);   opacity: 1; }
        }
        .order-card { animation: orderCardSlide 0.45s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .order-card:nth-child(1) { animation-delay: 0ms;   }
        .order-card:nth-child(2) { animation-delay: 60ms;  }
        .order-card:nth-child(3) { animation-delay: 120ms; }
        .order-card:nth-child(4) { animation-delay: 180ms; }
        .order-card:nth-child(5) { animation-delay: 240ms; }
        .modal-backdrop { animation: modalBackdropIn 0.2s ease both; }
        .modal-content  { animation: modalSlideUp 0.35s cubic-bezier(0.22, 1, 0.36, 1) both; }
        .status-badge   { animation: badgePop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both; }
        .tab-btn { transition: all 0.2s ease; }
        .tab-btn:hover { background: #f9fafb; }
        .payment-btn { transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .payment-btn:hover  { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(16,185,129,0.3); }
        .payment-btn:active { transform: scale(0.97); }
      `}</style>

      <main className="flex-grow">

        {/* ── Page Header ──────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">My Orders</h1>
                <p className="text-gray-600">Track and manage your recent purchases</p>
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search orders..."
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabs ─────────────────────────────────────────────────────── */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex overflow-x-auto gap-1 -mb-px">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`tab-btn px-4 md:px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeTab === tab.key
                      ? 'border-gray-900 text-gray-900'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.key ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Orders Content ────────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">

          {/* Ready to pay global banner */}
          {orders.filter(o => o.status?.toLowerCase() === 'ready to pay').length > 0 && (
            <div className="px-0 mb-5">
              <div style={{
                background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
                border: '1px solid #6ee7b7',
                borderRadius: 12,
                padding: '14px 20px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#059669', flexShrink: 0 }} />
                <strong style={{ color: '#065f46', fontSize: 14 }}>
                  You have {orders.filter(o => o.status?.toLowerCase() === 'ready to pay').length} order(s) ready for payment
                </strong>
              </div>
            </div>
          )}

          {filteredOrders.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders Found</h3>
              <p className="text-gray-600 mb-6">
                {searchQuery ? 'Try different search terms' : "You haven't placed any orders yet"}
              </p>
              <button
                onClick={() => navigate('/Shop/all')}
                className="bg-gray-900 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              >
                Start Shopping
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              {filteredOrders.map((order, orderIndex) => {
                const status = getStatusConfig(order.status);
                const StatusIcon = status.icon;

                return (
                  <div
                    key={order.id}
                    className="order-card bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow overflow-hidden"
                    style={{ animationDelay: `${orderIndex * 60}ms` }}
                  >
                    {/* Order Header */}
                    <div className="p-5 md:p-6 border-b border-gray-100">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-semibold text-lg text-gray-900">{order.id}</h3>
                          <span className={`status-badge inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.color}`}>
                            <StatusIcon size={13} />
                            {status.label}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(order.date).toLocaleDateString('en-KE', {
                            day: 'numeric', month: 'short', year: 'numeric'
                          })}
                        </div>
                      </div>
                    </div>

                    {/* ── PROGRESS BAR — compact card view, timeStamps from API ── */}
                    <div style={{ padding: '16px 20px 4px', background: '#fafafa', borderBottom: '1px solid #f3f4f6' }}>
                      <OrderProgressBar
                        status={order.status}
                        timeStamps={order.timeStamps}
                        compact={true}
                      />
                    </div>

                    {/* Ready to pay inline notice */}
                    {order.status?.toLowerCase() === 'ready to pay' && (
                      <div style={{ padding: '0 20px 0' }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                          border: '1px solid #6ee7b7',
                          borderRadius: 10,
                          padding: '10px 14px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 12,
                        }}>
                          <CreditCard size={15} color="#059669" />
                          <strong style={{ color: '#065f46', fontSize: 13 }}>This order is ready for payment</strong>
                        </div>
                      </div>
                    )}

                    {/* Products */}
                    <div className="p-5 md:p-6">
                      <div className="space-y-4 mb-6">
                        {order.products?.map((p, i) => (
                          <div key={i} className="flex gap-4">
                            <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                              <img
                                src={p.image || 'https://admins.junisaproducts.com/assets/images/icons/snacks.svg'}
                                alt={p.name}
                                className="w-full h-full object-cover"
                                onError={e => e.target.src = "https://placehold.co/80x80?text=?"}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-gray-900 mb-1 line-clamp-2">{p.name}</h4>
                              <div className="text-sm text-gray-500">
                                Qty: {p.quantity} × {formatCurrency(p.price)} = {formatCurrency(p.quantity * p.price)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-100">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Order Total</p>
                          <p className="text-xl font-bold text-gray-900">{formatCurrency(order.total)}</p>
                        </div>

                        <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                          {order.status?.toLowerCase() === 'ready to pay' && (
                            <button
                              onClick={() => {
                                setSelectedOrder(order);
                                setToPayId(order?.order_number);
                                setIsReadyToPay(true);
                              }}
                              className="payment-btn flex-1 sm:flex-none px-5 py-2.5 bg-green-700 rounded-lg text-sm font-medium flex items-center justify-center gap-2 text-white"
                            >
                              <CreditCard size={15} />
                              Make Payment
                            </button>
                          )}
                          <button
                            onClick={() => { setSelectedOrder(order); setIsReadyToPay(false); }}
                            className="flex-1 sm:flex-none px-5 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 text-gray-700"
                          >
                            <Eye size={15} />
                            Details
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ── ORDER DETAIL MODAL ───────────────────────────────────────── */}
        {selectedOrder && (
          <div
            className="modal-backdrop fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) setSelectedOrder(null); }}
          >
            <div className="modal-content bg-white rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">

              {/* Modal Header */}
              <div
                className="sticky top-0 bg-white border-b z-10 px-6 py-5 flex items-center justify-between"
                style={{ borderRadius: '16px 16px 0 0' }}
              >
                <div>
                  {isReadyToPay ? (
                    <h2 className="text-xl font-bold text-gray-900">Complete Your Payment</h2>
                  ) : (
                    <h2 className="text-xl font-bold text-gray-900">Order Details</h2>
                  )}
                  <p className="text-gray-500 text-sm mt-0.5">{selectedOrder.id}</p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <X size={22} className="text-gray-500" />
                </button>
              </div>

              <div className="p-6 space-y-6">

                {/* ── FULL PROGRESS BAR IN MODAL — timeStamps from API ── */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Order Progress</h3>
                  <div style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 16,
                    padding: '20px 16px 16px',
                  }}>
                    <OrderProgressBar
                      status={selectedOrder.status}
                      timeStamps={selectedOrder.timeStamps}
                      compact={false}
                    />
                  </div>
                </div>

                {/* Ready to pay CTA in modal */}
                {selectedOrder?.status?.toLowerCase() === 'ready to pay' && !isReadyToPay && (
                  <div style={{
                    background: 'linear-gradient(135deg, #ecfdf5, #d1fae5)',
                    border: '1px solid #6ee7b7',
                    borderRadius: 14,
                    padding: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16,
                  }}>
                    <div>
                      <div style={{ fontWeight: 700, color: '#065f46', fontSize: 14, marginBottom: 2 }}>
                        This order is awaiting payment
                      </div>
                      <div style={{ fontSize: 13, color: '#047857' }}>
                        Proceed to complete via M-Pesa
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setToPayId(selectedOrder?.order_number);
                        setIsReadyToPay(true);
                      }}
                      className="payment-btn px-5 py-2.5 bg-green-700 rounded-lg text-sm font-semibold flex items-center gap-2 text-white whitespace-nowrap"
                    >
                      <CreditCard size={15} />
                      Pay Now
                    </button>
                  </div>
                )}

                {/* M-Pesa Payment Form */}
                {isReadyToPay && (
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Payment</h3>

                    {/* Summary */}
                    <div className="bg-gray-50 rounded-xl p-5 space-y-3 mb-4">
                      <div className="flex justify-between text-gray-600 text-sm">
                        <span>Subtotal</span>
                        <span>{formatCurrency(selectedOrder.total - (selectedOrder.shipping_amount || 0))}</span>
                      </div>
                      <div className="flex justify-between text-gray-600 text-sm">
                        <span>Shipping</span>
                        <span>{formatCurrency(selectedOrder.Dmount || 0)}</span>
                      </div>
                      <div className="flex justify-between pt-3 border-t border-gray-200 font-bold text-base">
                        <span>Total</span>
                        <span>{formatCurrency(selectedOrder.total)}</span>
                      </div>
                    </div>

                    {/* Status messages */}
                    {mpesaLoading === 'failed-2' && (
                      <div style={{ background: '#fca5a5', color: '#7f1d1d', padding: '12px 16px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
                        {mpesaMessage}
                      </div>
                    )}
                    {mpesaLoading === 'success' && (
                      <div style={{ background: '#065f46', color: 'white', padding: '12px 16px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
                        ✓ Payment successful! Page will refresh shortly.
                      </div>
                    )}
                    {mpesaLoading === 'failed' && (
                      <div style={{ background: '#fca5a5', color: '#7f1d1d', padding: '12px 16px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
                        Something went wrong. Please retry.
                      </div>
                    )}
                    {mpesaLoading === 0 && (
                      <div style={{ background: '#dbeafe', color: '#1e40af', padding: '12px 16px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
                        Processing your payment — do not refresh.
                      </div>
                    )}
                    {mpesaLoading === 1 && (
                      <div style={{ background: '#d1fae5', color: '#065f46', padding: '12px 16px', borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
                        STK push sent to your phone. Enter M-Pesa PIN to complete.
                      </div>
                    )}

                    {/* Phone input */}
                    <div style={{
                      background: '#f0fdf4',
                      border: '1.5px solid #6ee7b7',
                      borderRadius: 14,
                      padding: 20,
                    }}>
                      <label style={{ display: 'block', fontWeight: 600, color: '#065f46', marginBottom: 8, fontSize: 13 }}>
                        M-Pesa Phone Number
                      </label>
                      <input
                        type="tel"
                        name="mpesaPhone"
                        value={formData.mpesaPhone}
                        onChange={handleInputChange}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1.5px solid #6ee7b7',
                          borderRadius: 10,
                          fontSize: 15,
                          outline: 'none',
                          background: 'white',
                          boxSizing: 'border-box',
                        }}
                        placeholder="0712345678"
                      />
                      <p style={{ fontSize: 12, color: '#047857', marginTop: 8 }}>
                        You'll receive an STK push to complete payment
                      </p>
                      <button
                        onClick={() => handleMpesaPrompt()}
                        className="payment-btn"
                        style={{
                          background: 'linear-gradient(135deg, #059669, #047857)',
                          color: 'white',
                          border: 'none',
                          padding: '13px 28px',
                          borderRadius: 10,
                          fontWeight: 700,
                          fontSize: 14,
                          marginTop: 14,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <CreditCard size={16} />
                        Make Payment
                      </button>
                    </div>
                  </div>
                )}

                {/* Items */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">
                    Items ({selectedOrder.products?.length || 0})
                  </h3>
                  <div className="space-y-3">
                    {selectedOrder.products?.map((p, i) => (
                      <div key={i} style={{
                        display: 'flex',
                        gap: 14,
                        padding: 14,
                        background: '#fafafa',
                        borderRadius: 12,
                        border: '1px solid #f3f4f6',
                      }}>
                        <div
                          className="rounded-lg overflow-hidden bg-gray-100 flex-shrink-0"
                          style={{ width: 72, height: 72 }}
                        >
                          <img
                            src={p.image || 'https://admins.junisaproducts.com/assets/images/icons/snacks.svg'}
                            alt={p.name}
                            className="w-full h-full object-cover"
                            onError={e => e.target.src = "https://placehold.co/72x72?text=?"}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, color: '#111827', fontSize: 14, marginBottom: 4 }}>{p.name}</div>
                          <div style={{ fontSize: 13, color: '#6b7280' }}>
                            {p.quantity} × {formatCurrency(p.price)} ={' '}
                            <strong style={{ color: '#374151' }}>{formatCurrency(p.quantity * p.price)}</strong>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Order Summary */}
                <div>
                  <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Summary</h3>
                  <div className="bg-gray-50 rounded-xl p-5 space-y-3">
                    <div className="flex justify-between text-gray-600 text-sm">
                      <span>Subtotal</span>
                      <span>{formatCurrency(selectedOrder.total - (selectedOrder.shipping_amount || 0))}</span>
                    </div>
                    <div className="flex justify-between text-gray-600 text-sm">
                      <span>Shipping</span>
                      <span>{formatCurrency(selectedOrder.Dmount || 0)}</span>
                    </div>
                    <div className="flex justify-between pt-3 border-t border-gray-200 font-bold text-base">
                      <span>Total</span>
                      <span>{formatCurrency(selectedOrder.total)}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        )}
      </main>

      <br /><br /><br />
      {footerConfig && <FooterSection config={footerConfig} />}
    </div>
  );
};

export default ShopPage;